import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  Dialog,
  TextInputField,
  FormField,
  Checkbox,
  Pane,
  Button,
  SelectField,
} from 'evergreen-ui';
import { Container, Row, Col } from 'react-grid-system';
import { putBank } from '../../services/api/banksService';
import { toastNotification } from '../../shared/toastNotification';
import { mutate } from 'swr';
import { bankAccountTypeArray, inputBankValidation } from '../../helpers/formHelper';
import { useQueryVitaUserByIdentityId } from '../../services/api/vitaUserService';

function BankAccountsEditFormComponent({ isShown, setIsShown, bankAccount = {} }) {
  const [isDisabled, setIsDisabled] = useState(true);
  const [checked, setChecked] = useState(false);
  const [isNegativeInitial, setIsNegativeInitial] = useState(false);

  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  // Get VitaUser UUID from Firebase UID
  const { data: vitaUser } = useQueryVitaUserByIdentityId(userId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { formState, register, handleSubmit, reset, setValue, watch } =
    useForm({
      defaultValues: bankAccount,
    });

  useEffect(() => {
    reset(bankAccount);
    setChecked(bankAccount.isPrincipal);

    const entry = bankAccount.initialBalanceEntry;
    if (entry) {
      const negative = !entry.isInput;
      setIsNegativeInitial(negative);
      setValue('isNegativeInitial', negative);
      setValue(
        'initialBalance',
        (parseFloat(entry.value) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
      );
      setValue('initialBalanceDate', entry.dateCompetencia?.substr(0, 10) || '');
    } else {
      setIsNegativeInitial(false);
      setValue('isNegativeInitial', false);
      setValue('initialBalance', '0,00');
      setValue('initialBalanceDate', new Date().toISOString().substr(0, 10));
    }

    console.log('bankAccount', bankAccount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    inputBankValidation(
      watch('agencyNumber'),
      watch('name'),
      watch('accountBankNumber'),
      watch('description'),
      watch('bankAccountType'),
      setIsDisabled
    );
  }, [
    watch('agencyNumber'),
    watch('name'),
    watch('accountBankNumber'),
    watch('description'),
    watch('bankAccountType'),
  ]);

  const bankAccountFormFooter = () => {
    return (
      <Pane>
        <Button
          form="banks-form"
          type="submit"
          width="fit-content"
          id="onSubmit"
          disabled={isDisabled}
        >
          Salvar
        </Button>
      </Pane>
    );
  };

  const onClose = () => {
    reset();
    setIsShown(false);
  };

  const handleInput = (event) => {
    let inputVal = event.target.value.replace(/\D/g, '');
    if (inputVal === '') {
      setValue('initialBalance', '');
      return;
    }
    inputVal = inputVal.padStart(3, '0');
    const wholeNum = inputVal.slice(0, -2);
    const decimal = inputVal.slice(-2);
    const formattedWholeNum = Number(wholeNum).toLocaleString('pt-BR');
    setValue('initialBalance', `${formattedWholeNum},${decimal}`);
  };

  const onSubmit = (bankAccount) => {
    console.log('onSubmit');
    let statusResponse = false;
    let toastContent = '';

    // Get VitaUser UUID (not Firebase UID)
    const vitaUserId = vitaUser?.data?.id;

    if (!vitaUserId) {
      toastNotification('warning', 'Erro: Usuário não identificado. Faça login novamente.');
      return;
    }

    const rawBalance = typeof bankAccount.initialBalance === 'string'
      ? bankAccount.initialBalance.includes(',')
        ? bankAccount.initialBalance.replace(/\./g, '').replace(',', '.') // "1.234,56" → "1234.56"
        : bankAccount.initialBalance // já está em formato numérico padrão ("1.00")
      : String(bankAccount.initialBalance || '0');

    let editedBankAccount = {
      isPrincipal: bankAccount.mainAccount,
      name: bankAccount.name,
      accountType: bankAccount.accountType,
      description: bankAccount.description,
      agencyNumber: bankAccount.agencyNumber,
      accountBankNumber: bankAccount.accountBankNumber,
      userUpdateId: vitaUserId,
      id: bankAccount.id,
      initialBalance: parseFloat(rawBalance) || 0,
      isNegativeInitial: isNegativeInitial,
      initialBalanceDate: bankAccount.initialBalanceDate || '',
    };

    console.log(editedBankAccount);

    putBank(editedBankAccount)
      .then((res) => {
        console.log('res put bank: ', res);
        mutate(`banks-${companyId}`);
        mutate('entriesFilteredByDate');
        mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
        mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
        statusResponse = 'success';
        toastContent = 'Conta alterada com sucesso.';
        onClose();
      })
      .catch((err) => {
        statusResponse = 'warning';
        console.log('post err', err);
        toastContent = `Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
      });
  };
  return (
    <Dialog
      isShown={isShown}
      title={'Editar Conta'}
      onCloseComplete={onClose}
      confirmLabel="Cadastrar"
      hasFooter={true}
      footer={bankAccountFormFooter}
      shouldCloseOnEscapePress={!formState.isDirty}
      width={600}
      backgroundColor="#f8f8f8"
    >
      <div>
        <form id="banks-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField>
            <Container
              fluid={true}
              style={{
                width: '100%',
                margin: 'none',
                padding: 'none',
              }}
            >
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <TextInputField
                    {...register('name')}
                    label="Nome"
                    id="name"
                    name="name"
                    type="text"
                  />
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register('accountType')}
                    label="Tipo de Conta"
                    onChange={(e) => setValue('bankAccountType', e.target.value)}
                  >
                    {bankAccountTypeArray.map((item) => {
                      return <option value={item}>{item}</option>;
                    })}
                  </SelectField>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <TextInputField
                    {...register('agencyNumber')}
                    label="Agência"
                    id="agencyNumber"
                    name="agencyNumber"
                    type="text"
                  />
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <TextInputField
                    {...register('accountBankNumber')}
                    label="Número da Conta"
                    id="accountBankNumber"
                    name="accountBankNumber"
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <TextInputField
                    {...register('description')}
                    label="Descrição"
                    id="description"
                    name="description"
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Checkbox
                    {...register('mainAccount')}
                    label="Conta Principal"
                    id="mainAccount"
                    name="mainAccount"
                    checked={checked}
                    onChange={(e) => {
                      setValue('mainAccount', e.target.checked);
                      setChecked(e.target.checked);
                      console.log(watch('mainAccount'));
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <TextInputField
                    {...register('initialBalance')}
                    label="Saldo Inicial (R$)"
                    id="initialBalance"
                    name="initialBalance"
                    type="text"
                    onChange={handleInput}
                  />
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <TextInputField
                    {...register('initialBalanceDate')}
                    label="Data do Saldo Inicial"
                    id="initialBalanceDate"
                    name="initialBalanceDate"
                    type="date"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Checkbox
                    {...register('isNegativeInitial')}
                    label="Saldo inicial negativo"
                    id="isNegativeInitial"
                    checked={isNegativeInitial}
                    onChange={(e) => {
                      setValue('isNegativeInitial', e.target.checked);
                      setIsNegativeInitial(e.target.checked);
                    }}
                  />
                </Col>
              </Row>
            </Container>
          </FormField>
        </form>
      </div>
    </Dialog>
  );
}

export default BankAccountsEditFormComponent;
